/**
 * Message board types — aligned's `channels`, `channel_memberships`,
 * `channel_read_receipts`, and `messages` collections (all top-level; keyed by
 * foreign ids, no subcollections). Reactions live in a separate `reactions`
 * collection and are deferred to a later slice — `MsgBoardMessage.reactions`
 * stays optional so it can be hydrated in-place then.
 *
 * Dates are stored/read as ISO strings (aligned's `@UTCDateTimeConverter`).
 */

// ---------------------------------------------------------------------------
// Enums (raw string values as stored in Firestore, matching Dart @JsonValue)
// ---------------------------------------------------------------------------

export type ChannelType = 'group' | 'direct_message';
export type ChannelRole = 'owner' | 'admin' | 'member';
export type MessageBoardUserType = 'member' | 'moderator' | 'employee';
export type NotificationPreference = 'immediate' | 'hourly' | 'daily' | 'never';
export const DEFAULT_CHANNEL_NOTIFICATION_PREFERENCE: NotificationPreference = 'hourly';
export const LEGACY_CHANNEL_NOTIFICATION_PREFERENCE: NotificationPreference = 'immediate';

export function isAnnouncementChannelName(name = ''): boolean {
  return /announce/i.test(name);
}

export function effectiveNotificationPreferenceForChannelName(
  preference: NotificationPreference,
  channelName?: string,
): NotificationPreference {
  return isAnnouncementChannelName(channelName) && preference !== 'never'
    ? 'immediate'
    : preference;
}

// ---------------------------------------------------------------------------
// Reaction — aligned's `reactions` collection, one doc per (message, emoji,
// user) triple. Toggling an existing reaction soft-deletes the doc (sets
// `deprecatedOn`) rather than removing it, matching aligned exactly.
// ---------------------------------------------------------------------------

export interface Reaction {
  id: string;
  messageId: string;
  emoji: string;
  authorId: string;
  firebaseId: string;
  authorEmail: string;
  createdAt: string | null;
  deprecatedOn: string | null;
}

// ---------------------------------------------------------------------------
// Channel
// ---------------------------------------------------------------------------

export interface Channel {
  id: string;
  name: string;
  description: string;
  ownerId: string;
  firebaseId: string;
  type: ChannelType;
  /** Sorted, immutable account ids for direct-message channels. */
  participantIds?: string[];
  /** Firebase auth ids used only by Firestore authorization rules. */
  participantFirebaseIds?: string[];
  /** Canonical identity for the complete DM participant set. */
  participantKey?: string | null;
  allowModeratorPosts: boolean;
  allowMemberPosts: boolean;
  /** Default used when a member is first added to this channel. */
  defaultNotificationPreference?: NotificationPreference;
  createdAt: string | null;
  updatedAt: string | null;
  deprecatedOn: string | null;
  lastMessageAt: string | null;
  relatedJobId: string | null;
  jobInstructions: string | null;
}

// ---------------------------------------------------------------------------
// ChannelMembership
// ---------------------------------------------------------------------------

export interface ChannelMembership {
  id: string;
  channelId: string;
  userId: string;
  firebaseId: string;
  userEmail: string;
  userName: string;
  role: ChannelRole;
  pushNotificationPreference: NotificationPreference;
  emailNotificationPreference: NotificationPreference;
  isModerator: boolean;
  /** Per-board employee designation; absent on legacy memberships. */
  isEmployee?: boolean;
  createdAt: string | null;
  deprecatedOn: string | null;
}

/** Match the company domain exactly, including case-insensitive imported emails. */
export function isAlignedEmployeeEmail(email?: string): boolean {
  return /^[^@\s]+@alignedhq\.ai$/i.test(email?.trim() ?? '');
}

/** Display identity, separate from channel ownership. Moderators and employees
 * share the moderator posting permission (`canUserSendGroupMessage`). */
export function messageBoardUserType(member?: ChannelMembership): MessageBoardUserType {
  if (isAlignedEmployeeEmail(member?.userEmail)) return 'employee';
  if (!member || member.deprecatedOn) return 'member';
  if (member.isEmployee) return 'employee';
  return member.isModerator ? 'moderator' : 'member';
}

// ---------------------------------------------------------------------------
// ChannelReadReceipt
// ---------------------------------------------------------------------------

export interface ChannelReadReceipt {
  id: string;
  channelId: string;
  userId: string;
  firebaseId: string;
  lastReadAt: string | null;
  createdAt: string | null;
  deprecatedOn: string | null;
  lastReadMessageId: string | null;
  lastEmailedForMessageId: string | null;
}

// ---------------------------------------------------------------------------
// MsgBoardMessage
// ---------------------------------------------------------------------------

export interface MsgBoardMessage {
  id: string;
  channelId: string;
  authorId: string;
  firebaseId: string;
  authorEmail: string;
  authorUserName: string;
  content: string;
  parentMessageId: string | null;
  replyCount: number | null;
  attachmentUrls: string[];
  /** Hydrated separately from the `reactions` collection; deferred slice. */
  reactions: unknown[] | null;
  createdAt: string | null;
  updatedAt: string | null;
  deprecatedOn: string | null;
}

export interface MessageBoardAttachment {
  url: string;
  fileName: string;
  mediaType: 'image' | 'video' | 'document';
  isImage: boolean;
}

// ---------------------------------------------------------------------------
// Mappers — tolerant to missing/wrong-typed fields (matching aligned's
// `.fromJson` behavior via freezed defaults).
// ---------------------------------------------------------------------------

type Data = Record<string, unknown>;

const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
const strOrNull = (v: unknown): string | null => {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object' && 'toDate' in v && typeof (v as { toDate?: unknown }).toDate === 'function') {
    const date = (v as { toDate: () => Date }).toDate();
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  return v instanceof Date && !Number.isNaN(v.getTime()) ? v.toISOString() : null;
};
const strList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
const numOrNull = (v: unknown): number | null => (typeof v === 'number' ? v : null);
const bool = (v: unknown, dflt: boolean): boolean => (typeof v === 'boolean' ? v : dflt);

function channelType(v: unknown): ChannelType {
  return v === 'direct_message' ? 'direct_message' : 'group';
}

function channelRole(v: unknown): ChannelRole {
  return v === 'owner' || v === 'admin' ? v : 'member';
}

function notificationPreference(
  v: unknown,
  fallback: NotificationPreference = 'immediate',
): NotificationPreference {
  return v === 'hourly' || v === 'daily' || v === 'never' || v === 'immediate' ? v : fallback;
}

export function mapChannel(id: string, data: Data): Channel {
  const type = channelType(data.type);
  return {
    id,
    name: str(data.name) ?? '',
    description: str(data.description) ?? '',
    ownerId: str(data.ownerId) ?? '',
    firebaseId: str(data.firebaseId) ?? '',
    type,
    ...(type === 'direct_message' || data.participantIds !== undefined || data.participantKey !== undefined
      ? {
        participantIds: strList(data.participantIds).sort(),
        participantFirebaseIds: strList(data.participantFirebaseIds).sort(),
        participantKey: str(data.participantKey) ?? null,
      }
      : {}),
    allowModeratorPosts: bool(data.allowModeratorPosts, true),
    allowMemberPosts: bool(data.allowMemberPosts, true),
    defaultNotificationPreference: notificationPreference(
      data.defaultNotificationPreference ?? data.defaultNotificationTiming,
      LEGACY_CHANNEL_NOTIFICATION_PREFERENCE,
    ),
    createdAt: strOrNull(data.createdAt),
    updatedAt: strOrNull(data.updatedAt),
    deprecatedOn: strOrNull(data.deprecatedOn),
    lastMessageAt: strOrNull(data.lastMessageAt),
    relatedJobId: strOrNull(data.relatedJobId),
    jobInstructions: strOrNull(data.jobInstructions),
  };
}

export function mapChannelMembership(id: string, data: Data): ChannelMembership {
  return {
    id,
    channelId: str(data.channelId) ?? '',
    userId: str(data.userId) ?? '',
    firebaseId: str(data.firebaseId) ?? '',
    userEmail: str(data.userEmail) ?? '',
    userName: str(data.userName) ?? '',
    role: channelRole(data.role),
    pushNotificationPreference: notificationPreference(data.pushNotificationPreference),
    emailNotificationPreference: notificationPreference(data.emailNotificationPreference),
    isModerator: bool(data.isModerator, false),
    isEmployee: bool(data.isEmployee, false),
    createdAt: strOrNull(data.createdAt),
    deprecatedOn: strOrNull(data.deprecatedOn),
  };
}

export function mapChannelReadReceipt(id: string, data: Data): ChannelReadReceipt {
  return {
    id,
    channelId: str(data.channelId) ?? '',
    userId: str(data.userId) ?? '',
    firebaseId: str(data.firebaseId) ?? '',
    lastReadAt: strOrNull(data.lastReadAt),
    createdAt: strOrNull(data.createdAt),
    deprecatedOn: strOrNull(data.deprecatedOn),
    lastReadMessageId: strOrNull(data.lastReadMessageId),
    lastEmailedForMessageId: strOrNull(data.lastEmailedForMessageId),
  };
}

export function mapReaction(id: string, data: Data): Reaction {
  return {
    id,
    messageId: str(data.messageId) ?? '',
    emoji: str(data.emoji) ?? '',
    authorId: str(data.authorId) ?? '',
    firebaseId: str(data.firebaseId) ?? '',
    authorEmail: str(data.authorEmail) ?? '',
    createdAt: strOrNull(data.createdAt),
    deprecatedOn: strOrNull(data.deprecatedOn),
  };
}

export function mapMsgBoardMessage(id: string, data: Data): MsgBoardMessage {
  return {
    id,
    channelId: str(data.channelId) ?? '',
    authorId: str(data.authorId) ?? '',
    firebaseId: str(data.firebaseId) ?? '',
    authorEmail: str(data.authorEmail) ?? '',
    authorUserName: str(data.authorUserName) ?? '',
    content: str(data.content) ?? '',
    parentMessageId: strOrNull(data.parentMessageId),
    replyCount: numOrNull(data.replyCount),
    attachmentUrls: strList(data.attachmentUrls),
    reactions: null,
    createdAt: strOrNull(data.createdAt),
    updatedAt: strOrNull(data.updatedAt),
    deprecatedOn: strOrNull(data.deprecatedOn),
  };
}

// ---------------------------------------------------------------------------
// Canonical read-receipt doc id — aligned uses
// `rr_<base64url(userId\nchannelId) with padding stripped>`, so each
// (user, channel) has exactly one deterministic doc regardless of concurrent
// writes. Ported verbatim from `message_board_service.dart:_readReceiptDocumentId`.
// ---------------------------------------------------------------------------

export function canonicalReadReceiptId(userId: string, channelId: string): string {
  const raw = `${userId}\n${channelId}`;
  const b64 =
    typeof btoa === 'function'
      ? btoa(raw)
      : Buffer.from(raw, 'utf8').toString('base64');
  const b64url = b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return `rr_${b64url}`;
}

/**
 * Field set for a fresh reaction doc, ported from aligned's
 * `FirestoreApiService.addReactionToMessage`. `nowIso` is passed in for
 * testability.
 */
export function buildNewReactionDoc(params: {
  messageId: string;
  emoji: string;
  authorId: string;
  firebaseId: string;
  authorEmail: string;
  nowIso: string;
}): Record<string, unknown> {
  return {
    messageId: params.messageId,
    emoji: params.emoji,
    authorId: params.authorId,
    firebaseId: params.firebaseId,
    authorEmail: params.authorEmail,
    createdAt: params.nowIso,
    deprecatedOn: null,
  };
}

/**
 * Field set for a fresh message doc, ported from aligned's `sendMessage`.
 * The `id` field is not set here — `createDoc` mints the document id and
 * writes it as part of the same write. `nowIso` is passed in for testability.
 */
export function buildNewMessageDoc(params: {
  channelId: string;
  authorId: string;
  firebaseId: string;
  authorEmail: string;
  authorUserName: string;
  content: string;
  parentMessageId: string | null;
  attachmentUrls: string[];
  nowIso: string;
}): Record<string, unknown> {
  const {
    channelId,
    authorId,
    firebaseId,
    authorEmail,
    authorUserName,
    content,
    parentMessageId,
    attachmentUrls,
    nowIso,
  } = params;
  return {
    channelId,
    authorId,
    firebaseId,
    authorEmail,
    authorUserName,
    content,
    parentMessageId,
    attachmentUrls,
    createdAt: nowIso,
    updatedAt: nowIso,
    deprecatedOn: null,
  };
}

/** The notification-tracking fields a read-receipt write must preserve from
 *  whatever receipt (if any) already existed for this (user, channel). */
export interface ReadReceiptPreservedFields {
  lastEmailedForMessageId?: string | null;
  lastEmailedAt?: string | null;
  lastPushNotifiedForMessageId?: string | null;
  lastPushNotifiedAt?: string | null;
  createdAt?: string | null;
}

/**
 * Field set for a read-receipt `setDoc` (create-or-update path), ported from
 * aligned's `createOrUpdateReadReceiptWithTransaction`. `nowIso` is passed in
 * (not read from `Date.now()`) so this stays pure and testable.
 *
 * `preserveFrom` carries the notification-tracking fields off the prior
 * receipt (or `null`/absent for a fresh doc) — a read-receipt write must
 * NEVER touch `lastEmailedForMessageId`/`lastEmailedAt`/push-notified fields
 * itself; only the server-side notification sender advances those. An
 * earlier version of this function unconditionally stamped
 * `lastEmailedForMessageId` to `lastReadMessageId` on every write, which
 * reproduced a real aligned bug (fixed upstream in `ff5f05589`) where marking
 * a channel read rolled back notification-sender state and caused
 * missed/duplicate email notifications. `createdAt` is likewise preserved
 * from the prior receipt when present, falling back to `nowIso` for a fresh doc.
 */
export function buildReadReceiptDoc(params: {
  canonicalId: string;
  channelId: string;
  userId: string;
  firebaseId: string;
  lastReadMessageId: string;
  nowIso: string;
  preserveFrom?: ReadReceiptPreservedFields | null;
}): Record<string, unknown> {
  const { canonicalId, channelId, userId, firebaseId, lastReadMessageId, nowIso, preserveFrom } = params;
  return {
    id: canonicalId,
    channelId,
    userId,
    firebaseId,
    deprecatedOn: null,
    lastReadAt: nowIso,
    lastReadMessageId,
    lastEmailedForMessageId: preserveFrom?.lastEmailedForMessageId ?? null,
    lastEmailedAt: preserveFrom?.lastEmailedAt ?? null,
    lastPushNotifiedForMessageId: preserveFrom?.lastPushNotifiedForMessageId ?? null,
    lastPushNotifiedAt: preserveFrom?.lastPushNotifiedAt ?? null,
    createdAt: preserveFrom?.createdAt ?? nowIso,
  };
}
